import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, ChevronRight, Search, BookOpen, AlertCircle, RefreshCw } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';

const API_BASE = "https://pw-api-proxy.dev-rahulmaida.workers.dev";

// --- UTILS ---
const getImageUrl = (urlData: any) => {
  if (!urlData) return "https://via.placeholder.com/300x169?text=No+Image";
  
  let url = "";
  if (typeof urlData === 'string') {
    url = urlData;
  } else if (typeof urlData === 'object') {
    if (urlData.baseUrl && urlData.key) {
      url = urlData.baseUrl + urlData.key;
    } else if (urlData.key) {
      url = urlData.key;
    }
  }

  if (!url) return "https://via.placeholder.com/300x169?text=Broken+Image";
  if (url.startsWith('http')) return url;
  
  if (url.startsWith('cdn5/')) {
    return `https://cdn.penpencil.co/${url}`;
  }
  return `https://static.pw.live/${url}`;
};

// --- COMPONENTS ---

const ErrorView = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center p-12 bg-red-500/10 border border-red-500/20 rounded-2xl">
    <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
    <h3 className="text-xl font-bold text-white mb-2">Something went wrong</h3>
    <p className="text-gray-400 text-center max-w-md">{message}</p>
    <button 
      onClick={() => window.location.reload()}
      className="mt-6 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full font-bold transition-all"
    >
      <RefreshCw className="w-4 h-4" /> Retry
    </button>
  </div>
);

const BatchCard = ({ batch }: { batch: any }) => {
  const navigate = useNavigate();
  return (
    <div className="glass-card group flex flex-col h-full bg-[#1E1E2E]/50 border border-white/10 rounded-2xl overflow-hidden hover:border-[#5A4FCF]/50 transition-all duration-300">
      <div className="relative aspect-video">
        <img 
          src={getImageUrl(batch.previewImage || batch.previewImageUrl)}
          alt={batch.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e: any) => { e.target.src = "https://via.placeholder.com/300x169?text=PW+Batch"; }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-[#5A4FCF] p-3 rounded-full">
            <Play className="w-6 h-6 fill-white" />
          </div>
        </div>
      </div>
      
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-bold text-lg mb-2 line-clamp-2">{batch.name}</h3>
        <p className="text-sm text-gray-400 line-clamp-2 mb-4">
          {batch.byName || "PW Special Batch"}
        </p>
        
        <div className="mt-auto flex justify-between items-center">
          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded font-bold">UNLOCKED</span>
          <button 
            onClick={() => navigate(`/batch/${batch._id}`)}
            className="flex items-center text-[#5A4FCF] font-bold hover:gap-2 transition-all"
          >
            Open <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchBatches();
  }, [activeTab]);

  const fetchBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = activeTab === "all" ? "/api/all-batches?page=1" : "/v3/batches/my-batches";
      const res = await axios.get(`${API_BASE}${endpoint}`);
      setBatches(res.data.data || []);
      setLoading(false);
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 401) {
        setError("Your Login Token has EXPIRED. Please provide a fresh Authorization Token.");
      } else {
        setError("Failed to fetch batches. Check your internet or proxy.");
      }
      setLoading(false);
    }
  };

  const filteredBatches = batches.filter(b => 
    b.name && b.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0F0F1E] text-white p-4 md:p-8">
      <header className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Rahul maida free study
          </h1>
          <p className="text-gray-400 mt-2 font-medium">May 4, 2026 Update - Premium Batches Unlocked</p>
        </div>
        
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <div className="flex bg-[#1E1E2E] p-1 rounded-full border border-white/5">
            <button 
              onClick={() => setActiveTab("all")}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${activeTab === 'all' ? 'bg-[#5A4FCF] text-white' : 'text-gray-400'}`}
            >
              All Batches (Paid)
            </button>
            <button 
              onClick={() => setActiveTab("my")}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${activeTab === 'my' ? 'bg-[#5A4FCF] text-white' : 'text-gray-400'}`}
            >
              My Batches
            </button>
          </div>
          
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input 
              type="text"
              placeholder="Search batches..."
              className="w-full bg-[#1E1E2E] border border-white/10 rounded-full py-3 pl-10 pr-4 focus:outline-none focus:border-[#5A4FCF]"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto">
        {error ? (
          <ErrorView message={error} />
        ) : loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A4FCF]"></div>
            <p className="text-gray-500 animate-pulse">Decrypting Paid Content...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBatches.map((batch) => (
              <BatchCard key={batch._id} batch={batch} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const BatchDetailsPage = () => {
  const { batchId } = useParams();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const res = await axios.get(`${API_BASE}/v2/batches/${batchId}/subjects`);
        setSubjects(res.data.data || []);
        setLoading(false);
      } catch (err: any) {
        console.error(err);
        if (err.response?.status === 401) {
          setError("Token Expired. Please update your token.");
        } else {
          setError("Failed to load subjects.");
        }
        setLoading(false);
      }
    };
    fetchSubjects();
  }, [batchId]);

  return (
    <div className="min-h-screen bg-[#0F0F1E] text-white p-4 md:p-8">
      <nav className="max-w-7xl mx-auto mb-8">
        <Link to="/" className="text-[#5A4FCF] font-bold flex items-center gap-2">
          ← Back to All Batches
        </Link>
      </nav>

      <main className="max-w-7xl mx-auto">
        {error ? (
          <ErrorView message={error} />
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A4FCF]"></div>
          </div>
        ) : (
          <>
            <header className="mb-12">
              <h1 className="text-3xl font-bold">Select Subject</h1>
              <p className="text-gray-400 mt-2">All content is unlocked for your account</p>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {subjects.map((sub) => (
                <div 
                  key={sub._id} 
                  className="glass-card p-6 flex items-center gap-4 cursor-pointer hover:bg-[#5A4FCF]/10"
                >
                  <div className="bg-[#5A4FCF]/20 p-4 rounded-xl text-[#5A4FCF]">
                    <BookOpen className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl">{sub.subject}</h3>
                    <p className="text-gray-400 text-sm">View Lectures & Notes</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/batch/:batchId" element={<BatchDetailsPage />} />
      </Routes>
    </Router>
  );
};

export default App;
